import { type MutationOptions, useMutation } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { addActionNameToMutationResult } from 'shared/hooks/utils/add-action-name-to-mutation-result';
import { useAxiosInstance } from 'shared/hooks/utils/axios-instance';
import type { ApiError } from 'shared/hooks/utils/types';
import { z } from 'zod/v4';

const buildUrl = (threadId: string) => `mails/threads/${threadId}/generate-draft`;

const GenerateDraftSchema = z.object({
  threadId: z.string(),
  userPrompt: z.string(),
});

export type GenerateDraftVariables = z.infer<typeof GenerateDraftSchema>;

const GENERATE_DRAFT_STATUSES = ['error', 'success'] as const;

const GenerateDraftResponseSchema = z.object({
  body: z.string(),
  status: z.enum([...GENERATE_DRAFT_STATUSES]),
});

export type GenerateDraftResponse = z.infer<typeof GenerateDraftResponseSchema>;

async function generateDraftFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: GenerateDraftVariables,
): Promise<GenerateDraftResponse> {
  const { threadId, userPrompt } = GenerateDraftSchema.parse(variables);
  const instance = await axiosInstance;
  const response = await instance.post(buildUrl(threadId), { userPrompt });

  return GenerateDraftResponseSchema.parse(response.data);
}

export function useGenerateDraft(
  options: MutationOptions<GenerateDraftResponse, ApiError, GenerateDraftVariables> = {},
) {
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<GenerateDraftResponse, ApiError, GenerateDraftVariables>({
    mutationFn: variables => generateDraftFn(axiosInstance, variables),
    ...options,
  });

  return addActionNameToMutationResult<
    GenerateDraftResponse,
    ApiError,
    GenerateDraftVariables,
    'generateDraft'
  >('generateDraft', mutation);
}
